import React, { useMemo } from 'react';
import { useAnnotations } from './AnnotationProvider';
import AnnotationCard from './AnnotationCard';
import AnnotationComposer from './AnnotationComposer';
import usePolling from 'shared/hooks/usePolling';
import {
  listConversationAnnotations,
  listRunAnnotations,
  listSpanAnnotations,
  sortAnnotations,
} from './annotationSelectors';

/**
 * Shared list used by conversation, run, and span drawers.
 *
 * `scope` decides which rows appear:
 *   all  — every annotation on the conversation (conversation drawer)
 *   run  — run-level plus span-level annotations for spans inside the run
 *   span — context.type === 'span' && (span_id, started_at)
 *
 * `lockedContext` is passed straight into the composer — the user never picks it.
 *
 * Comments read oldest-first with the composer under them, the way every
 * comment thread does; the composer used to sit above the list, so the box
 * you type in was nowhere near the reply you were answering.
 *
 * `layout` is 'panel' for a drawer that owns its own scroll, or 'section'
 * for an inline block inside a page that already scrolls — which is how
 * comments now appear at the foot of a run's or span's Activity.
 *
 * Slack-originated replies are inserted on the backend; poll while this list
 * is open so they appear without a full page reload. The provider drops a
 * poll that changed nothing, so a quiet thread costs no renders. 5s, backing
 * off to 30s while the list sits open, back to 5s whenever the tab is looked
 * at again, and nothing while the tab is hidden.
 */
const SLACK_REPLY_POLL_MS = 5000;
const SLACK_REPLY_MAX_POLL_MS = 30000;

export default function AnnotationList({
  conversationId,
  scope = 'all',
  runId,
  spanId,
  spanStartedAt,
  lockedContext,
  onOpenContext,
  layout = 'panel',
}) {
  const {
    enabled,
    annotations,
    currentUser,
    loadConversation,
    loadRun,
    createAnnotation,
    replyToAnnotation,
    updateAnnotation,
    deleteAnnotation,
  } = useAnnotations();

  usePolling(() => {
    // listForRun returns the run's comments AND its spans' comments, so a
    // span-scoped list loads by run too — one request for the whole run
    // rather than one per span as you click through the trace.
    if (runId) return loadRun(runId, conversationId).catch(() => {});
    if (!conversationId) return undefined;
    return loadConversation(conversationId).catch(() => {});
  }, {
    enabled,
    interval: SLACK_REPLY_POLL_MS,
    maxInterval: SLACK_REPLY_MAX_POLL_MS,
    key: `${scope}|${runId || ''}|${conversationId || ''}`,
  });

  const items = useMemo(() => {
    let list;
    if (scope === 'run') list = listRunAnnotations(annotations, runId);
    else if (scope === 'span') list = listSpanAnnotations(annotations, spanId, spanStartedAt);
    else list = listConversationAnnotations(annotations, conversationId);
    return sortAnnotations(list);
  }, [annotations, scope, conversationId, runId, spanId, spanStartedAt]);

  if (!enabled) return null;

  // Show which run or span a comment is on only where the list actually spans
  // several — at conversation scope, and in the chat. On one span they would
  // all say the same thing.
  const showContext = scope !== 'span';

  /**
   * One container, not four.
   *
   * Comments sat in a bordered card, inside a bordered section, inside the
   * bordered tab panel, with the reply box in a fifth border nested in the
   * card — worst at conversation level. But stripping every frame leaves the
   * comments floating with nothing to say where one ends. So the list is
   * grouped by hairlines rather than boxes, and the only bordered thing is
   * the composer, which should look like something you type into.
   */
  const list = (
    <div className="flex flex-col divide-y divide-[color:var(--border-subtle)]">
      {items.map((ann) => (
        <div key={ann.id} className="py-3 first:pt-0 last:pb-0">
          <AnnotationCard
            annotation={ann}
            currentUser={currentUser}
            onReply={replyToAnnotation}
            onEdit={updateAnnotation}
            onDelete={deleteAnnotation}
            onOpenContext={onOpenContext}
            showContext={showContext}
          />
        </div>
      ))}
    </div>
  );

  const composer = (
    <AnnotationComposer
      autoFocus={false}
      context={lockedContext}
      conversationId={conversationId}
      placeholder="Leave an annotation..."
      submitLabel="Annotate"
      onSubmit={(payload) => {
        createAnnotation({
          conversationId: payload.conversationId,
          context: lockedContext,
          body: payload.body,
          mentions: payload.mentions,
        }).catch(() => {});
      }}
    />
  );

  if (layout === 'section') {
    return (
      <div className="flex flex-col gap-3">
        {items.length ? list : null}
        {composer}
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto px-3 pt-3">
        {items.length === 0 ? (
          <p className="py-6 text-center text-[12px] text-[color:var(--text-muted)]">
            No annotations yet.
          </p>
        ) : list}
      </div>
      <div className="flex-none px-3 pb-3 pt-2">{composer}</div>
    </div>
  );
}
